/**
 * Equipment type master — catalog used when members list assets.
 */

export const EQUIPMENT_CATEGORIES = [
  'Agriculture',
  'Construction',
  'Haulage',
  'Utility',
  'Material handling',
  'Other',
];

export const VEHICLE_CLASSES = [
  'LMV',
  'HMV',
  'Construction Equipment',
  'Stationary Equipment',
];

export const REQUIREMENT_LEVELS = [
  { value: 'required', label: 'Required' },
  { value: 'optional', label: 'Optional' },
  { value: 'hidden', label: 'Hidden' },
];

/** Custom docs only need required / optional (delete instead of hide). */
export const CUSTOM_DOC_LEVELS = [
  { value: 'required', label: 'Required' },
  { value: 'optional', label: 'Optional' },
];

export const EQUIPMENT_FIELD_KEYS = [
  { key: 'regNo', label: 'Registration number' },
  { key: 'chassisNumber', label: 'Chassis number' },
  { key: 'engineNumber', label: 'Engine number' },
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'model', label: 'Model' },
  { key: 'variant', label: 'Variant' },
  { key: 'fuel', label: 'Fuel type' },
  { key: 'color', label: 'Color' },
  { key: 'capacity', label: 'Capacity' },
  { key: 'capacityUom', label: 'Capacity unit' },
  { key: 'yom', label: 'Year of manufacture' },
  { key: 'nameAsPerRc', label: 'Name as per RC' },
  { key: 'rcValidUntil', label: 'RC valid until' },
  { key: 'pucValidUntil', label: 'PUC valid until' },
  { key: 'insuranceValidUntil', label: 'Insurance valid until' },
  { key: 'fitnessValidUntil', label: 'Fitness valid until' },
  { key: 'taxValidUntil', label: 'Tax valid until' },
];

export const EQUIPMENT_DOC_TYPES = [
  { key: 'photo_front', label: 'Photo — front' },
  { key: 'photo_back', label: 'Photo — back' },
  { key: 'photo_left', label: 'Photo — left' },
  { key: 'photo_right', label: 'Photo — right' },
  { key: 'rc_copy', label: 'RC copy' },
  { key: 'puc_photo', label: 'PUC document' },
  { key: 'insurance_copy', label: 'Insurance copy' },
  { key: 'fitness_photo', label: 'Fitness photo' },
];

export function defaultFieldRequirements() {
  const map = {};
  EQUIPMENT_FIELD_KEYS.forEach(({ key }) => {
    map[key] = 'optional';
  });
  map.manufacturer = 'required';
  map.model = 'required';
  map.fuel = 'required';
  map.color = 'required';
  map.regNo = 'required';
  return map;
}

export function defaultDocRequirements() {
  const map = {};
  EQUIPMENT_DOC_TYPES.forEach(({ key }) => {
    map[key] = 'optional';
  });
  map.photo_front = 'required';
  map.photo_back = 'required';
  map.puc_photo = 'required';
  return map;
}

export function defaultCustomDocs() {
  return [];
}

function slugify(label) {
  return String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

export function isPlaceholderCustomDocKey(key) {
  return /^c_doc_\d+$/i.test(String(key || '').trim());
}

/** Readable slug from document name, e.g. "Heavy lift certificate" → heavy_lift_certificate */
export function makeTempCustomDocKey(label, existingKeys = []) {
  const set = new Set(existingKeys);
  EQUIPMENT_DOC_TYPES.forEach(({ key }) => set.add(key));
  const base = slugify(label) || 'custom_doc';
  let key = base.slice(0, 50);
  if (set.has(key)) {
    key = `custom_${base}`.slice(0, 50);
  }
  let n = 2;
  while (set.has(key)) {
    const suffix = `_${n}`;
    key = `custom_${base}`.slice(0, 50 - suffix.length) + suffix;
    n += 1;
  }
  return key;
}

/** Build API payload for doc checklist (builtin levels + custom docs). */
export function buildDocRequirementsPayload(docRequirements, customDocs = []) {
  const payload = { ...(docRequirements || defaultDocRequirements()) };
  const used = new Set(Object.keys(payload).filter((k) => k !== 'customDocs'));
  EQUIPMENT_DOC_TYPES.forEach(({ key }) => used.add(key));

  const cleaned = (customDocs || [])
    .map((d) => {
      const label = (d.label || '').trim();
      if (!label) return null;
      let key = (d.key || '').trim();
      if (!key || isPlaceholderCustomDocKey(key)) {
        key = makeTempCustomDocKey(label, [...used]);
      }
      used.add(key);
      return {
        key,
        label,
        level: d.level === 'required' ? 'required' : 'optional',
      };
    })
    .filter(Boolean);
  payload.customDocs = cleaned;
  return payload;
}

/** Parse API row into form docRequirements + customDocs. */
export function parseDocRequirementsFromApi(row) {
  const docRequirements = {
    ...defaultDocRequirements(),
    ...(row?.docRequirements || {}),
  };
  // Strip non-level keys if present
  delete docRequirements.customDocs;

  const fromTypes = Array.isArray(row?.docTypes)
    ? row.docTypes.filter((d) => d && d.builtin === false)
    : [];
  const fromPayload = Array.isArray(row?.docRequirements?.customDocs)
    ? row.docRequirements.customDocs
    : [];

  const customDocs = (fromTypes.length ? fromTypes : fromPayload).map((d) => ({
    key: d.key || '',
    label: d.label || '',
    level: d.level === 'required' ? 'required' : 'optional',
  }));

  return { docRequirements, customDocs };
}

/** Offline / mock fallback seed (API mode uses `/equipment-types`). */
export const equipmentMasterSeed = [
  {
    equipmentId: 'EQ-001',
    name: 'Tractor',
    category: 'Agriculture',
    vehicleClass: 'LMV',
    description: 'Agricultural tractor',
    active: true,
  },
  {
    equipmentId: 'EQ-002',
    name: 'Trailer',
    category: 'Agriculture',
    vehicleClass: 'HMV',
    description: 'Tractor trailer / trolley',
    active: true,
  },
  {
    equipmentId: 'EQ-003',
    name: 'Backhoe Loader',
    category: 'Construction',
    vehicleClass: 'Construction Equipment',
    description: '',
    active: true,
  },
  {
    equipmentId: 'EQ-004',
    name: 'Excavator',
    category: 'Construction',
    vehicleClass: 'Construction Equipment',
    description: '',
    active: true,
  },
  {
    equipmentId: 'EQ-005',
    name: 'Dumper',
    category: 'Haulage',
    vehicleClass: 'HMV',
    description: '',
    active: true,
  },
  {
    equipmentId: 'EQ-006',
    name: 'Truck',
    category: 'Haulage',
    vehicleClass: 'HMV',
    description: '',
    active: true,
  },
  {
    equipmentId: 'EQ-007',
    name: 'Water Tanker',
    category: 'Utility',
    vehicleClass: 'HMV',
    description: '',
    active: true,
  },
  {
    equipmentId: 'EQ-008',
    name: 'Crane',
    category: 'Construction',
    vehicleClass: 'Construction Equipment',
    description: '',
    active: true,
  },
];
